import { useState, useEffect } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import API from '../../services/apiService'
import { useConfirm } from '../ConfirmModal/ConfirmModal'
import { Skeleton } from '../Skeleton/Skeleton'
import './GMManager.css'

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16">
    <defs>
      <linearGradient id="searchGr" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fcd34d" />
        <stop offset="100%" stopColor="#d97706" />
      </linearGradient>
    </defs>
    <circle cx="10" cy="10" r="6" stroke="url(#searchGr)" strokeWidth="2" fill="none" />
    <path d="M14.5 14.5L20 20" stroke="url(#searchGr)" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

const CrownIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18">
    <defs>
      <linearGradient id="crownGr" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fcd34d" />
        <stop offset="100%" stopColor="#d97706" />
      </linearGradient>
    </defs>
    <path d="M2 20h20L18 8l-6 4-6-4-4 12z" fill="url(#crownGr)" stroke="#92400e" strokeWidth="0.8" />
    <circle cx="6" cy="8" r="2" fill="url(#crownGr)" stroke="#92400e" strokeWidth="0.6" />
    <circle cx="12" cy="8" r="2" fill="url(#crownGr)" stroke="#92400e" strokeWidth="0.6" />
    <circle cx="18" cy="8" r="2" fill="url(#crownGr)" stroke="#92400e" strokeWidth="0.6" />
  </svg>
)

const formatDate = (d) => {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function GMManager() {
  const { t } = useTranslation()
  const [gms, setGms] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState({ name: '', notes: '', isActive: true })
  const confirm = useConfirm()

  const fetchData = async () => {
    setLoading(true)
    setError(null)
    try {
      const [gmRes, statsRes] = await Promise.all([
        API.GMs.list(1, 100),
        API.GMs.getStats()
      ])
      setGms(Array.isArray(gmRes.data) ? gmRes.data : [])
      setStats(statsRes.data || statsRes)
    } catch (err) {
      setError(err.message || t('admin.gmLoadFail'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchData() }, [])

  const filtered = gms.filter(g =>
    !search || g.name?.toLowerCase().includes(search.toLowerCase()) ||
    g.notes?.toLowerCase().includes(search.toLowerCase())
  )

  const handleSave = async () => {
    if (!form.name?.trim()) { setError(t('admin.gmNameRequired')); return }
    setError(null); setSuccess(null)
    try {
      if (editing) {
        await API.GMs.update(editing, form)
        setSuccess(t('admin.gmUpdateSuccess'))
      } else {
        await API.GMs.upsert(form)
        setSuccess(t('admin.gmCreateSuccess'))
      }
      setEditing(null)
      setForm({ name: '', notes: '', isActive: true })
      fetchData()
    } catch (err) {
      setError(err.message || t('admin.gmSaveFail'))
    }
  }

  const handleDelete = async (id, name) => {
    if (!await confirm(t('admin.gmDeleteConfirm', { name }), t('admin.gmDeleteUndoable'))) return
    try {
      await API.GMs.delete(id)
      setSuccess(t('admin.gmDeleteSuccess', { name }))
      fetchData()
    } catch (err) {
      setError(err.message || t('admin.gmDeleteFail'))
    }
  }

  const startEdit = (gm) => {
    setEditing(gm.id)
    setForm({
      name: gm.name || '',
      notes: gm.notes || '',
      isActive: gm.isActive !== false
    })
  }

  if (loading) return (
    <div className="gm-manager">
      <div className="gm-manager__header"><h3><CrownIcon /> {t('admin.gmStats')}</h3></div>
      <Skeleton height="120px" />
    </div>
  )

  return (
    <div className="gm-manager">
      <div className="gm-manager__header">
        <h3><CrownIcon /> {t('admin.gmStats')}</h3>
      </div>

      {error && <div className="gm-manager__error">{error}</div>}
      {success && <div className="gm-manager__success">{success}</div>}

      {stats && (
        <div className="gm-manager__stats">
          <div className="gm-manager__stat"><span className="gm-manager__stat-value">{stats.total || 0}</span>{t('admin.totalGMs')}</div>
          <div className="gm-manager__stat"><span className="gm-manager__stat-value">{stats.active || 0}</span>{t('admin.activeGMs')}</div>
          <div className="gm-manager__stat"><span className="gm-manager__stat-value">{stats.inactive || 0}</span>{t('admin.gmInactive')}</div>
          <div className="gm-manager__stat"><span className="gm-manager__stat-value">{stats.recentlySeen || 0}</span>{t('admin.onlineNow')}</div>
          <div className="gm-manager__stat"><span className="gm-manager__stat-value">{stats.seenLast24Hours || 0}</span>{t('admin.seenToday')}</div>
        </div>
      )}

      <div className="gm-manager__search-bar">
        <SearchIcon />
        <input
          type="text"
          placeholder={t('admin.search')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="gm-manager__search-input"
        />
      </div>

      <div className="gm-manager__form">
        <h4>{editing ? t('admin.editGM') : t('admin.addGM')}</h4>
        <div className="gm-manager__form-fields">
          <input
            placeholder={t('admin.gmName') + ' *'}
            value={form.name}
            onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            className="gm-manager__input"
          />
          <textarea
            placeholder={t('admin.gmNotes')}
            value={form.notes}
            onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
            className="gm-manager__textarea"
            rows={2}
          />
          <div className="gm-manager__form-row">
            <label className="ann-manager__checkbox-label">
              <input type="checkbox" checked={form.isActive} onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))} />
              {t('admin.gmActive')}
            </label>
          </div>
        </div>
        <div className="gm-manager__form-actions">
          <button className="gm-manager__btn gm-manager__btn--save" onClick={handleSave}>
            {editing ? t('admin.editGM') : t('admin.addGM')}
          </button>
          {editing && (
            <button className="gm-manager__btn gm-manager__btn--cancel" onClick={() => {
              setEditing(null)
              setForm({ name: '', notes: '', isActive: true })
            }}>{t('account.cancel')}</button>
          )}
        </div>
      </div>

      <div className="gm-manager__list">
        {filtered.length === 0 ? (
          <p className="gm-manager__empty">{t('admin.noData')}</p>
        ) : (
          filtered.map(gm => (
            <div key={gm.id} className="gm-manager__item">
              <div className="gm-manager__item-info">
                <div className="gm-manager__item-name">
                  <CrownIcon />
                  <strong>{gm.name}</strong>
                  <span className={`gm-manager__item-status gm-manager__item-status--${gm.isActive ? 'active' : 'inactive'}`}>
                    {gm.isActive ? t('admin.gmActive') : t('admin.gmInactive')}
                  </span>
                </div>
                {gm.notes && <p className="gm-manager__item-desc">{gm.notes}</p>}
                <div className="gm-manager__item-meta">
                  {gm.lastSeenAt && <span>{t('admin.lastUpdated')} {formatDate(gm.lastSeenAt)}</span>}
                  {gm.lastSeenLocation && <span>{t('admin.map')}: {gm.lastSeenLocation}</span>}
                  {gm.createdAt && <span>{t('admin.createdShort')}: {formatDate(gm.createdAt)}</span>}
                </div>
              </div>
              <div className="gm-manager__item-actions">
                <button className="gm-manager__btn gm-manager__btn--edit" onClick={() => startEdit(gm)}>{t('admin.editGM')}</button>
                <button className="gm-manager__btn gm-manager__btn--delete" onClick={() => handleDelete(gm.id, gm.name)}>{t('admin.deleteGM')}</button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
